/**
 * c6-serp-demo — SERP explorer data for the query
 * "best running shoes for flat feet". All brands, domains, figures and
 * reviews are invented demo data; the slide carries a persistent
 * "Demo data" badge.
 */

export interface SerpCallout {
  label: string;
  text: string;
}

export interface SerpRegion {
  id: string;
  n: number;
  /** Explainer panel title. */
  name: string;
  /** Single-paragraph explainer body (all regions except R5). */
  body?: string;
  /** R5 only — four stacked sub-callouts. */
  callouts?: SerpCallout[];
}

export const SERP_REGIONS: SerpRegion[] = [
  {
    id: "query",
    n: 1,
    name: "The query",
    body:
      "Everything below is Google's answer to these six words. There is no fixed recipe for \"#1\" — results depend on the query, your region, your device, the competition, and Google's quality systems.",
  },
  {
    id: "sponsored",
    n: 2,
    name: "Sponsored results",
    body:
      "Paid ads, marked 'Sponsored', sitting above everything organic. Before the first ranked result even appears, ads have already changed what \"first\" means on this page. When the budget stops, so do these.",
  },
  {
    id: "shopping",
    n: 3,
    name: "Shopping results",
    body:
      "Product cards pulled from merchant feeds and structured data — price, image, rating. Another block pushing the blue links down, and another reason a raw position number tells you very little.",
  },
  {
    id: "ai-overview",
    n: 4,
    name: "AI Overview",
    body:
      "Google's AI answers the question right here — on some queries, not all. It cites sources, so helpful, well-structured content can still win the mention. Sometimes no one clicks anything below it.",
  },
  {
    id: "organic-hero",
    n: 5,
    name: "The #1 organic result",
    callouts: [
      {
        label: "Title link",
        text: "Written in the page's title tag — the biggest on-page click-through lever. And it holds #1 for this query, in this region, on this device. Change any of those and the page changes.",
      },
      {
        label: "URL breadcrumb",
        text: "Google shows the site structure. Clean, readable URLs earn trust before the click.",
      },
      {
        label: "Meta description",
        text: "The pitch under the title. It doesn't move ranking — it decides whether humans click. Google rewrites it if it's bad.",
      },
      {
        label: "Sitelinks",
        text: "Bonus links Google adds for sites with clear structure. Extra real estate — earned, not configured.",
      },
    ],
  },
  {
    id: "rich-result",
    n: 6,
    name: "Rich result",
    body:
      "Stars, review counts and price ranges come from structured data in the page's code. Same rank, twice the eye-catch — \"first\" is about presentation as much as position.",
  },
  {
    id: "paa",
    n: 7,
    name: "People Also Ask",
    body:
      "Real questions real people search. Each answer is pulled from a page that answered it clearly. This box is a free content brief — answer these well and you can be the source.",
  },
  {
    id: "local-pack",
    n: 8,
    name: "Local pack",
    body:
      "For local-intent searches, Google swaps in a map and business profiles. Same words on a different region or device = a completely different page. \"We rank #3\" means little without that context.",
  },
  {
    id: "related",
    n: 9,
    name: "Related searches",
    body:
      "Where people go when this page didn't nail it. Free keyword research — and a reminder that a single vanity keyword is never the whole battle.",
  },
];

/* ---------------------------------------------------------------- mock content */

export interface AdResult {
  advertiser: string;
  url: string;
  title: string;
  desc: string;
}

export const AD_RESULTS: AdResult[] = [
  {
    advertiser: "RunDepot",
    url: "rundepot.example/stability",
    title: "RunDepot — Stability Shoes From $89",
    desc: "Shop stability running shoes for flat feet. Free returns and fast delivery.",
  },
  {
    advertiser: "PacePoint Sports",
    url: "pacepoint.example/flat-feet",
    title: "PacePoint — Flat Feet? Start Here",
    desc: "Supportive running shoes fitted to your stride. Book a free gait check today.",
  },
];

export interface ShoppingProduct {
  name: string;
  price: string;
  rating: number;
  retailer: string;
  /** Accent colour of the CSS-drawn shoe stripe. */
  tone: string;
}

export const SHOPPING_PRODUCTS: ShoppingProduct[] = [
  { name: "Strider Arch 3", price: "$129", rating: 4.5, retailer: "stride.example", tone: "#22d3ee" },
  { name: "RoadStable GT", price: "$89", rating: 4.1, retailer: "RunDepot", tone: "#818cf8" },
  { name: "MotionGuard 12", price: "$159", rating: 4.6, retailer: "PacePoint Sports", tone: "#a78bfa" },
  { name: "FlexArch Pro", price: "$109", rating: 4.3, retailer: "SoleHouse", tone: "#34d399" },
  { name: "TerraSupport X", price: "$189", rating: 4.7, retailer: "FleetFoot Gear", tone: "#fbbf24" },
];

export const SITELINKS = [
  "Top Picks by Budget",
  "Wide Fit Options",
  "Arch Support Basics",
  "How We Test",
];

export interface PaaItem {
  q: string;
  /** First row is pre-expanded with a snippet + source. */
  answer?: string;
  source?: string;
}

export const PAA_ITEMS: PaaItem[] = [
  {
    q: "Are stability shoes good for flat feet?",
    answer:
      "Often, yes. Stability shoes add structure under the arch to limit overpronation — the inward roll that's common with flat feet.",
    source: "strideguide.example › stability-shoes",
  },
  { q: "Do flat feet need arch support?" },
  { q: "How long do running shoes last?" },
  { q: "Can I run with flat feet?" },
];

export interface LocalStore {
  name: string;
  rating: number;
}

export const LOCAL_STORES: LocalStore[] = [
  { name: "Stride Running Co.", rating: 4.8 },
  { name: "The Runner's Room", rating: 4.6 },
  { name: "Citypace Sports", rating: 4.4 },
];

export const RELATED_SEARCHES = [
  "best stability running shoes 2026",
  "flat feet running shoes women",
  "motion control vs stability",
  "running shoe fitting near me",
];
